import { forwardRef } from 'react';
import { SelectField, type SelectFieldProps, type SelectOption } from '@/components/form';
import { useBrands, useCategories } from '@/data/hooks';

type PickerProps = Omit<SelectFieldProps, 'label' | 'options'> & { label?: string };

interface Row {
  id: string;
  name: string;
  active: boolean;
}

/**
 * Options from the loaded rows: only active ones, plus the product's current value so it is never
 * silently dropped: an inactive one shows its name, one that is gone shows a plain notice.
 */
function toOptions(rows: Row[] | undefined, current?: string): SelectOption[] {
  const options: SelectOption[] = (rows ?? [])
    .filter((r) => r.active)
    .map((r) => ({ value: r.id, label: r.name }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
  if (current && !options.some((o) => o.value === current)) {
    const stored = rows?.find((r) => r.id === current);
    const label = !rows ? 'Cargando…' : stored ? `${stored.name} (inactiva)` : 'No disponible (ya no existe)';
    // Disabled: it stays visible as the current value but cannot be picked again once changed.
    options.push({ value: current, label, disabled: true });
  }
  return options;
}

/** Display name for a stored id, for read-only screens (never shows the raw id). */
function nameFor(rows: Row[] | undefined, id: string): string {
  if (!rows) return '…';
  const row = rows.find((r) => r.id === id);
  if (!row) return 'No disponible';
  return row.active ? row.name : `${row.name} (inactiva)`;
}

export function BrandName({ id }: { id: string }) {
  const { data } = useBrands();
  return <>{nameFor(data?.map((b) => ({ id: b.brandId, name: b.name, active: b.active })), id)}</>;
}

export function CategoryName({ id }: { id: string }) {
  const { data } = useCategories();
  return <>{nameFor(data?.map((c) => ({ id: c.categoryId, name: c.name, active: c.active })), id)}</>;
}

export const BrandSelect = forwardRef<HTMLSelectElement, PickerProps & { current?: string }>(function BrandSelect(
  { label = 'Marca', current, ...rest },
  ref,
) {
  const { data } = useBrands();
  const rows = data?.map((b) => ({ id: b.brandId, name: b.name, active: b.active }));
  return <SelectField ref={ref} label={label} placeholder="Selecciona una marca" options={toOptions(rows, current)} {...rest} />;
});

export const CategorySelect = forwardRef<HTMLSelectElement, PickerProps & { current?: string }>(
  function CategorySelect({ label = 'Categoría', current, ...rest }, ref) {
    const { data } = useCategories();
    const rows = data?.map((c) => ({ id: c.categoryId, name: c.name, active: c.active }));
    return (
      <SelectField ref={ref} label={label} placeholder="Selecciona una categoría" options={toOptions(rows, current)} {...rest} />
    );
  },
);
